import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEventName } from '@/api/analytics';
import {
  playerBonusesCancellation,
  playerFreespinsCancellation,
} from '@/api/common';
import { Button, ButtonClose } from '@/components/buttons';
import { Modal } from '@/components/Modal';
import { showTooltip } from '@/helpers/tooltip';
import { useAnalytics } from '@/hooks/analytics';
import { useCommonStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScCancelBonusModal, ScCancelBonusModalContainer } from './styled';

export interface CancelBonusModalProps {
  isBonusType: boolean;
  id: number;
}

export const CancelBonusModal: FC<CancelBonusModalProps> = ({
  isBonusType,
  id,
}) => {
  const fetchBonuses = useCommonStore.use.fetchBonuses();
  const fetchCompoints = useCommonStore.use.fetchCompoints();
  const { t } = useTranslation('bonuses');
  const { sendEventToAnalytics } = useAnalytics();

  const [pending, setPending] = useState(false);

  const bonusType = isBonusType ? 'bonus' : 'freespin';

  const onClose = useCallback(
    () =>
      useCommonStore.setState({
        currentCancelBonusData: undefined,
      }),
    []
  );

  const handleCancelBonus = useCallback(async () => {
    const error = await (async () => {
      let responseError;

      setPending(true);

      if (isBonusType) {
        const { error } = await playerBonusesCancellation(id);

        if (!error) await fetchBonuses();

        responseError = error;
      } else {
        const { error } = await playerFreespinsCancellation(id);

        if (!error) await fetchCompoints();

        responseError = error;
      }

      return responseError;
    })();

    if (error) {
      await sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__BONUS_CARD__CANCEL__FAILURE,
        attributes: {
          additionalData: JSON.stringify({
            type: bonusType,
            id,
          }),
        },
      });

      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('sthWentWrong'),
      });
    } else {
      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: t('tooltip.bonusCancelSuccess'),
      });

      await sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__BONUS_CARD__CANCEL__SUCCESS,
        attributes: {
          additionalData: JSON.stringify({
            type: bonusType,
            id,
          }),
        },
      });
    }

    setPending(false);

    onClose();
  }, [
    onClose,
    isBonusType,
    id,
    fetchBonuses,
    fetchCompoints,
    sendEventToAnalytics,
    bonusType,
    t,
  ]);

  return (
    <Modal>
      <ScCancelBonusModalContainer>
        <ScCancelBonusModal>
          <ButtonClose top={8} right={8} dark onClick={onClose} />

          <div className="text-content">
            <div className="title">{t('cancelBonusModal.title')}</div>
            <div className="warning">{t('cancelBonusModal.descr')}</div>
          </div>

          <div className="btn-container">
            <Button
              blackTransparent
              noMinWidth
              wide
              disabled={pending}
              onClick={handleCancelBonus}
            >
              {t('cancelBonusModal.yes')}
            </Button>
            <Button
              wide
              noMinWidth
              primary
              disabled={pending}
              onClick={onClose}
            >
              {t('cancelBonusModal.no')}
            </Button>
          </div>
        </ScCancelBonusModal>
      </ScCancelBonusModalContainer>
    </Modal>
  );
};
